import { Translate } from 'next-translate';

import { Global } from '@utils/common';
import { CustomColumnHeader, CustomColumnItem, ID, LineItem, Lot, LotChanges, QualQuestion } from 'types';
import { mergeLots } from '@components/bid_request/details/utils';

import { normalizeLineItemPrice } from '@components/bid_request/details/utils/normalizePrice';
import {
  getCustomColumnItemPrefix,
  getHeaderPrefix,
  getLineItemPrefix,
  getLotPrefix,
  messageNoAssociatedCriteriaId,
  messageNoLineItems,
} from './validateMessages';
import { FieldTypes, validatesPresenceOf } from './validatesPresenceOf';

export const lotFields: Record<string, FieldTypes> = {
  line_items: 'array',
  name: 'string',
  sno: 'number',
};
export const lineItemFields: Record<string, FieldTypes> = {
  extended_quantity: 'number',
  lot_id: 'string | number',
  name: 'string',
  quantity: 'number',
  sno: 'number',
  units: 'string',
};
export const customColumnItemFields: Record<string, FieldTypes> = {
  column_type: 'string',
};
export const customColumnHeaderFields: Record<string, FieldTypes> = {
  column_type: 'string',
  display_text: 'string',
  lot_id: 'string | number',
};

export const qualQuestionFields: Record<string, FieldTypes> = {
  id: 'string | number',
  question_type: 'string',
  text: 'string',
};

const validateLineItem = (t: Translate, prefix: string, lot: Lot, lineItem: LineItem): void => {
  // Ensure line item price is in valid format, since we are skipping validations for price.
  const cleanLineItem = normalizeLineItemPrice(lineItem);

  validatesPresenceOf(t, prefix, cleanLineItem, 'line_item', lineItemFields);
  const items = cleanLineItem.custom_column_items;

  if (!items?.length) return;
  items.forEach((customColumnItem: CustomColumnItem): void => {
    const itemPrefix = getCustomColumnItemPrefix(t, lot, cleanLineItem, customColumnItem);

    validatesPresenceOf(t, itemPrefix, customColumnItem, 'custom_column_item', customColumnItemFields);
  });
};

const validateQualQuestions = (t: Translate, headers: CustomColumnHeader[], lot: Lot): void => {
  if (!headers?.length) return;

  headers.forEach((header: CustomColumnHeader): void => {
    if (!header.qual_question) return;

    const headerPrefix = getHeaderPrefix(t, lot, header);
    const qualQuestion:QualQuestion = header.qual_question;

    validatesPresenceOf(t, headerPrefix, qualQuestion, 'qual_question', qualQuestionFields);
  });
};

const validateCustomColumnHeaders = (t: Translate, lot: Lot): void => {
  const headers = lot.custom_column_headers;
  const nonDescriptiveColumnHeaders: CustomColumnHeader[] = [];

  if (!headers?.length) return;

  headers.forEach((header: CustomColumnHeader): void => {
    if ( header.column_type !== Global.lots.custom_column_headers.column_types.descriptive ){
      nonDescriptiveColumnHeaders.push(header);
      return;
    }

    const headerPrefix = getHeaderPrefix(t, lot, header);

    validatesPresenceOf(t, headerPrefix, header, 'custom_column_header', customColumnHeaderFields);
  });

  if ( nonDescriptiveColumnHeaders.length )
    validateQualQuestions(
      t,
      nonDescriptiveColumnHeaders,
      lot,
    );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _validateIds = (t: Translate, prefix: string, ids: ID[]): void => {
  if (!ids?.length) return;
  const message = `${prefix}: [${messageNoAssociatedCriteriaId(t)}]`;

  ids.forEach((id: ID): void => {
    if (id) return;

    throw new Error(message);
  });
};

const validateLineItems = (t: Translate, prefix: string, lot: Lot): void => {
  const lineItems: LineItem[] = lot.line_items;

  if (!lineItems?.length) throw new Error(`${prefix}: ${messageNoLineItems(t)}`);
  lineItems.forEach((lineItem: LineItem): void => {
    const lineItemPrefix = getLineItemPrefix(t, lot, lineItem);

    validateLineItem(t, lineItemPrefix, lot, lineItem);
  });
};

const validateLot = (t: Translate, prefix: string, lot: Lot): void => {
  validatesPresenceOf(t, prefix, lot, 'lot', lotFields);
  validateLineItems(t, prefix, lot);
  validateCustomColumnHeaders(t, lot);
};

const validateLots = (t: Translate, lots: Lot[]): void => {
  if (!lots?.length) return;
  lots.forEach((lot: Lot): void => {
    const lotPrefix = getLotPrefix(t, lot);

    validateLot(t, lotPrefix, lot);
  });
};

export const validateBeforeSaveChanges = ({ t, lots, lotChanges }: {
  lotChanges?: Record<ID, LotChanges>;
  lots?: Lot[];
  t: Translate;
}): void => {
  validateLots(t, mergeLots({ lotChanges, lots }));
};
